import { supabase } from './supabase';

export interface StreakData {
  currentStreak: number;
  longestStreak: number;
  lastActivityDate: string | null;
  todayCompleted: boolean;
  streakAtRisk: boolean;
}

export async function calculateStreak(userId: string, nativeLanguage: string): Promise<StreakData> {
  const tableName = nativeLanguage === 'Persian' ? 'persian_lesson_progress' : 'lesson_progress';

  try {
    const { data, error } = await supabase
      .from(tableName)
      .select('updated_at')
      .eq('user_id', userId)
      .eq('completed', true)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Streak calculation error:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      console.log('Streak: No completed lessons found for user', userId);
      return {
        currentStreak: 0,
        longestStreak: 0,
        lastActivityDate: null,
        todayCompleted: false,
        streakAtRisk: false
      };
    }

    const uniqueDates = getUniqueDates(data.map(d => d.updated_at));
    const today = getDateString(new Date());
    const yesterday = getDateString(new Date(Date.now() - 86400000));

    console.log('Streak calculation:', { userId, today, yesterday, uniqueDates: uniqueDates.slice(0, 5) });

    const todayCompleted = uniqueDates.includes(today);
    const lastActivityDate = uniqueDates[0] || null;

    let currentStreak = 0;
    let checkDate = todayCompleted ? today : yesterday;

    for (let i = 0; i < uniqueDates.length; i++) {
      if (uniqueDates[i] === checkDate) {
        currentStreak++;
        checkDate = getDateString(new Date(new Date(checkDate).getTime() - 86400000));
      } else if (uniqueDates[i] < checkDate) {
        break;
      }
    }

    if (!todayCompleted && lastActivityDate !== yesterday) {
      currentStreak = 0;
    }

    let longestStreak = 0;
    let tempStreak = 1;

    for (let i = 1; i < uniqueDates.length; i++) {
      const prevDate = new Date(uniqueDates[i - 1]);
      const currDate = new Date(uniqueDates[i]);
      const diffDays = Math.floor((prevDate.getTime() - currDate.getTime()) / 86400000);

      if (diffDays === 1) {
        tempStreak++;
      } else {
        longestStreak = Math.max(longestStreak, tempStreak);
        tempStreak = 1;
      }
    }
    longestStreak = Math.max(longestStreak, tempStreak, currentStreak);

    const streakAtRisk = currentStreak > 0 && !todayCompleted;

    const result = {
      currentStreak,
      longestStreak,
      lastActivityDate,
      todayCompleted,
      streakAtRisk
    };

    console.log('Streak result:', result);
    return result;
  } catch (err) {
    console.error('Error calculating streak:', err);
    return {
      currentStreak: 0,
      longestStreak: 0,
      lastActivityDate: null,
      todayCompleted: false,
      streakAtRisk: false
    };
  }
}

function getDateString(date: Date): string {
  return date.toISOString().split('T')[0];
}

function getUniqueDates(timestamps: string[]): string[] {
  const dates = timestamps.map(ts => getDateString(new Date(ts)));
  return [...new Set(dates)].sort((a, b) => b.localeCompare(a));
}

export function getStreakMessage(streak: number, todayCompleted: boolean, streakAtRisk: boolean): string {
  if (streak === 0 && !todayCompleted) {
    return "Start your streak today!";
  }

  if (streakAtRisk) {
    return `${streak} day streak - Practice today to keep it going!`;
  }

  if (streak === 1) {
    return "You started a streak! Keep it going tomorrow!";
  }

  if (streak < 7) {
    return `${streak} day streak - You're building momentum!`;
  }

  if (streak < 30) {
    return `${streak} day streak - Amazing dedication!`;
  }

  if (streak < 100) {
    return `${streak} day streak - You're unstoppable!`;
  }

  return `${streak} day streak - Legendary learner!`;
}

export function getStreakEmoji(streak: number): string {
  if (streak === 0) return "";
  if (streak < 3) return "";
  if (streak < 7) return "";
  if (streak < 14) return "";
  if (streak < 30) return "";
  if (streak < 100) return "";
  return "";
}
